<template>
  <modal
    :show="isVisible"
    :title="modalTitle"
    elem-id="modal_crud_so-production-steps"
    size="small"
    :close-label="t('Cancelar')"
    :save-label="isDeleteMode ? (isDeleting ? t('Deletando') : t('Deletar')) : (isSaving ? t('Salvando') : t('Salvar'))"
    :save-fn="isDeleteMode ? handleDelete : handleSave"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <div v-if="isDeleteMode" class="delete-confirmation">
        <p>{{ t('Tem certeza que deseja deletar esta etapa de produção?') }}</p>
        <p class="text-muted">{{ itemToDelete?.so_production_step_name }}</p>
      </div>

      <div v-else>
        <s-o-production-steps-form
          ref="formRef"
          :itemId="currentId"
          :isEditMode="isEditMode"
          @submit-success="handleFormSuccess"
          @submit-error="handleFormError"
        />
        <div v-if="isEditMode" class="delete-action">
          <Button
            data-testid="so-production-steps-delete-button"
            severity="danger"
            outlined
            icon="pi pi-trash"
            :label="t('Deletar')"
            @click="enterDeleteConfirmation"
          />
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOCatalog } from '../../composables/useSOCatalog';
import { eventBus } from '../../eventBus';
import SOProductionStepsForm from './SOProductionStepsForm.vue';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface ProductionStepData {
  so_production_step_id?: Id;
  so_production_step_name?: string;
  [key: string]: unknown;
}

interface SOProductionStepsFormInstance {
  resetForm: () => void;
  loadForEdit: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  getItemName: () => string;
}

interface ModalOpenPayload {
  id?: Id;
  data?: ProductionStepData | null;
  action?: 'create' | 'edit' | 'delete';
}

const { t } = useI18n();
const { remove } = useSOCatalog('production_steps');

const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const isDeleteMode = ref<boolean>(false);
const currentId = ref<Id | null>(null);
const itemToDelete = ref<ProductionStepData | null>(null);
const formRef = ref<SOProductionStepsFormInstance | null>(null);
const isSaving = ref<boolean>(false);
const isDeleting = ref<boolean>(false);

const modalTitle = computed<string>(() => {
  if (isDeleteMode.value) return t('Deletar Etapa de Produção');
  if (isEditMode.value) return t('Editar Etapa de Produção');
  return t('Adicionar Etapa de Produção');
});

const openForCreate = (): void => {
  log('SOProductionStepsModal', 'openForCreate', 'Opening modal for create');
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentId.value = null;
  nextTick(() => { if (formRef.value) formRef.value.resetForm(); });
};

const openForEdit = async (id: Id): Promise<void> => {
  log('SOProductionStepsModal', 'openForEdit', 'Opening modal for edit', id);
  isVisible.value = true;
  isEditMode.value = true;
  isDeleteMode.value = false;
  currentId.value = id;
  await nextTick();
  if (formRef.value) await formRef.value.loadForEdit();
};

const openForDelete = async (id: Id, data: ProductionStepData | null = null): Promise<void> => {
  log('SOProductionStepsModal', 'openForDelete', 'Opening modal for delete', id);
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = true;
  currentId.value = id;
  itemToDelete.value = data;
};

const enterDeleteConfirmation = (): void => {
  log('SOProductionStepsModal', 'enterDeleteConfirmation', 'Switching to delete confirmation', currentId.value);
  itemToDelete.value = { so_production_step_name: formRef.value?.getItemName() ?? '' };
  isEditMode.value = false;
  isDeleteMode.value = true;
};

const handleClose = (): void => {
  isVisible.value = false;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentId.value = null;
  itemToDelete.value = null;
  isSaving.value = false;
  isDeleting.value = false;
  if (formRef.value) formRef.value.resetForm();
};

const handleSave = async (): Promise<void> => {
  if (formRef.value) {
    isSaving.value = true;
    try { await formRef.value.handleSubmit(); } finally { isSaving.value = false; }
  }
};

const handleDelete = async (): Promise<void> => {
  if (!currentId.value) return;
  isDeleting.value = true;
  try {
    await remove({ so_production_step_id: currentId.value });
    eventBus.emit('globalMessage.success', t('Etapa de produção deletada com sucesso'));
    eventBus.emit('so-production-steps.deleted');
    handleClose();
  } catch (error: unknown) {
    logError('SOProductionStepsModal', 'handleDelete', 'Error', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao deletar etapa de produção') + ': ' + message);
  } finally {
    isDeleting.value = false;
  }
};

const handleFormSuccess = (): void => {
  eventBus.emit('so-production-steps.saved');
  handleClose();
};

const handleFormError = (error: unknown): void => {
  logError('SOProductionStepsModal', 'handleFormError', 'Form error', error);
};

const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  log('SOProductionStepsModal', 'handleModalOpen', 'Modal open event', data);
  if (data?.action === 'delete' && data?.id) await openForDelete(data.id, data.data || null);
  else if (data?.id) await openForEdit(data.id);
  else openForCreate();
};

onMounted(() => {
  eventBus.on<ModalOpenPayload>('modal.so-production-steps.open', handleModalOpen);
  eventBus.on('modal.so-production-steps.close', handleClose);
});

onBeforeUnmount(() => {
  eventBus.off<ModalOpenPayload>('modal.so-production-steps.open', handleModalOpen);
  eventBus.off('modal.so-production-steps.close', handleClose);
});

defineExpose({ openForCreate, openForEdit, openForDelete });
</script>

<style scoped>
.delete-confirmation { padding: 20px; text-align: center; }
.delete-confirmation p { margin-bottom: 10px; font-size: 16px; }
.text-muted { color: #6c757d; font-style: italic; }
.delete-action { display: flex; justify-content: flex-end; margin-top: 1rem; padding-top: 1rem; border-top: 1px solid var(--app-bg-dark, #ccc); }
</style>
